import { useMemoizedFn } from 'ahooks';
import { history, useIntl, getLocale } from 'umi';
import React, { memo, useMemo } from 'react';
import { useGrowthDetails } from '../hooks';
import Loading from '@/pages/intelligence/components/Loading';
import EacherMap from '../../components/EchartsMap';
import { mathFormat } from '@/pages/home/utils/search';
import { echartMapTooltip } from '../utils';
import MarketDetailsBottom from './MarketDetailsBottom';
import CardSlot from '@/pages/intelligence/components/CardSlot';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import nameMap from '@/pages/dashboard/components/MapEchart/_config/nameMap.json';
import { financeFormat } from '@/pages/home/utils/common';
// 如果传递的泛型不是继承自其他组件的props,将返回never
export type ComponentProps<T> = T extends
  | React.ComponentType<infer P>
  | React.Component<infer P>
  ? JSX.LibraryManagedAttributes<T, P>
  : never;

const MarketEcharts = ({
  changeValue,
  type,
  newzooTitle,
}: ComponentProps<typeof MarketDetailsBottom>) => {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const isEn = locale === 'en-US';
  // Market Details数据,通过上下文catch Key 多次并发将会合并成一次请求
  const { data: marketData, loading: marketLoading } = useGrowthDetails(
    useMemo(
      () =>
        type
          ? {
            total_date: (+changeValue).toString(),
            start_date: (+changeValue - 1).toString(),
            end_date: (+changeValue).toString(),
          }
          : { total_date: (+changeValue).toString() },
      [changeValue, type],
    ),
    changeValue,
  );
  // 地图数据格式化
  const eacherMapList = useMemo(() => {
    const dataRes = marketData?.type_list;
    const mapRevenue = type ? marketData?.revenue : marketData?.player;
    const mapGrowth = type ? marketData?.growth : marketData?.payer;
    const mapColor = [
      'rgb(125, 173, 255)',
      'rgb(255, 171, 175)',
      'rgb(146, 222, 180)',
      'rgb(255, 193, 130)',
    ];
    const textFomat1= (value) => {
      return financeFormat(value, 0, false, '-');
    }
    const textFomat2= (value) => {
      return Number(Number(value)*100).toFixed(2) + '%';
    }
    const revenueLegend = [
      {
        type: 'Revenue+ & Growth+',
        value: 0,
        color: mapColor[0],
        hoverText: 'Stars 明星市场',
        text: (rev: string | number, growth: string | number) =>
          `Stars Revenue ≥$${rev}  Growth ≥${textFomat2(growth)}`,
        text_cn: (rev: string | number, growth: string | number) =>
          ` 明星市场  收入 ≥$${rev}  增长率 ≥${textFomat2(growth)}`,
      },
      {
        type: 'Revenue+ & Growth-',
        color: mapColor[1],
        value: 1,
        hoverText: 'Cash Cow 成熟市场',
        text: (rev: string | number, growth: string | number) =>
          `Cash Cow  Revenue ≥$${rev}  Growth ＜${textFomat2(growth)}`,
        text_cn: (rev: string | number, growth: string | number) =>
          `成熟市场  收入 ≥$${rev}  增长率 ＜${textFomat2(growth)}`,
      },
      {
        type: 'Revenue- & Growth+',
        color: mapColor[2],
        value: 2,
        hoverText: 'Question Mark 问号市场',
        text: (rev: string | number, growth: string | number) =>
          `Question Mark Revenue ＜$${rev}  Growth ≥${textFomat2(growth)}`,
        text_cn: (rev: string | number, growth: string | number) =>
          `问号市场  收入 ＜$${rev}  增长率 ≥${textFomat2(growth)}`,
      },
      {
        type: 'Revenue- & Growth-',
        color: mapColor[3],
        value: 3,
        hoverText: 'Dogs 瘦狗市场',
        text: (rev: string | number, growth: string | number) =>
          `Dogs Revenue ＜$${rev}  Growth ＜${textFomat2(growth)}`,
        text_cn: (rev: string | number, growth: string | number) =>
          `瘦狗市场  收入 ＜$${rev}  增长率 ＜${textFomat2(growth)}`,
      },
    ];
    const playersLegend = [
      {
        type: 'Players+ & Payers+',
        text: (player: string, payer: string) =>
          `Players ≥ ${player} & Payers ≥ ${textFomat1(payer)}`,
        text_cn: (player: string, payer: string) =>
          `玩家 ≥ ${player} & 付费玩家 ≥ ${textFomat1(payer)}`,
        value: 0, // 用于放在地图着色 值为0 1 2 3 ，对应颜色
        color: mapColor[0],
      },
      {
        type: 'Players+ & Payers-',
        text: (player: string, payer: string) =>
          `Players ≥ ${player} & Payers < ${textFomat1(payer)}`,
        text_cn: (player: string, payer: string) =>
          `玩家 ≥ ${player} & 付费玩家 < ${textFomat1(payer)}`,
        value: 1,
        color: mapColor[1],
      },
      {
        type: 'Players- & Payers+',
        text: (player: string, payer: string) =>
          `Players < ${player} & Payers ≥ ${textFomat1(payer)}`,
        text_cn: (player: string, payer: string) =>
          `玩家 < ${player} & 付费玩家 ≥ ${textFomat1(payer)}`,
        value: 2,
        color: mapColor[2],
      },
      {
        type: 'Players- & Payers-',
        text: (player: string, payer: string) =>
          `Players < ${player} & Payers < ${textFomat1(payer)}`,
        text_cn: (player: string, payer: string) =>
          `玩家 < ${player} & 付费玩家 < ${textFomat1(payer)}`,
        value: 3,
        color: mapColor[3],
      },
    ];
    const mapValueColor: any = {};
    const colorConfig = type ? revenueLegend : playersLegend;
    colorConfig.forEach((item) => {
      mapValueColor[item.type] = item.value;
    });
    const formattingData = () => {
      if (!Array.isArray(dataRes)) return;
      const mapData: any[] = [];
      dataRes?.map((item) => {
        const { type } = item;
        const { countrys } = item;
        Array.isArray(countrys) &&
          countrys?.forEach((country) => {
            mapData.push({
              ...(country || {}),
              //   TODO:中英文，特殊国家缺少，需要额外处理
              name: isEn ? country?.country : nameMap[country?.country],
              nameEn: country?.country_abbre,
              value: mapValueColor[type],
            });
          });
      });
      return mapData;
    };
    const visualMapColorFun = [
      // 自定义『分段式视觉映射组件（visualMapPiecewise）』的每一段的范围，以及每一段的文字，以及每一段的特别的样式
      { value: 3, color: mapColor[3] },
      { value: 2, color: mapColor[2] },
      { value: 1, color: mapColor[1] },
      { value: 0, color: mapColor[0] },
    ];
    return {
      dataRes: formattingData(),
      mapRevenue,
      mapGrowth,
      marketLegend: colorConfig,
      visualMapColorFun,
    };
  }, [
    marketData?.growth,
    marketData?.payer,
    marketData?.player,
    marketData?.revenue,
    marketData?.type_list,
    type,
    locale,
  ]);

  // 地图tooltip回调
  const tooltipMapFun = useMemoizedFn((param) => {
    if (!param?.data) {
      return null;
    }
    const letTitle = param?.name; // 左标题
    const rightTitle = '';
    const revenueNameListObj = {
      games_count: formatMessage({ id: 'dbi_global_view_game_count' }),
      revenue_growth: formatMessage({ id: 'dbi_global_view_revenue_growth' }),
      revenue: formatMessage?.({ id: 'dbi_revenue' }),
      revenue_per_global: formatMessage({
        id: 'dbi_global_view_revenue_global',
      }),
    };
    const playersNameListObj = {
      games_count: formatMessage({ id: 'dbi_global_view_game_count' }),
      revenue: formatMessage?.({ id: 'dbi_revenue' }),
      players: formatMessage?.({ id: 'dbi_global_view_players' }),
      payers: formatMessage?.({ id: 'dbi_global_view_payers' }),
    };
    const nameListConfig = type ? revenueNameListObj : playersNameListObj;
    const fun = (val: string) => {
      if (isNaN(parseInt(val, 10))) {
        return '-';
      }
      return mathFormat(val);
    };
    const fun2 = (val: string) => {
      if (!val || isNaN(parseFloat(val))) {
        return '-';
      }
      return `${parseFloat(parseFloat(val)?.toFixed(2))}%`;
    };
    const revenueFormatFunObj = {
      games_count: fun,
      revenue: fun,
      revenue_growth: fun2,
      revenue_per_global: fun2,
    };
    const playersFormatFunObj = {
      games_count: fun,
      revenue: fun,
      players: fun,
      payers: fun,
    };
    const formatFunObjConfig = type ? revenueFormatFunObj : playersFormatFunObj;
    return echartMapTooltip?.({
      param,
      letTitle,
      rightTitle,
      nameListObj: nameListConfig,
      formatFunObj: formatFunObjConfig,
    });
  });
  // 地图点击回调
  const handleClickFun = useMemoizedFn((param) => {
    if (!param?.data) return;
    // 待确定,profile页面doneing
    history.push({
      pathname: '/intelligence/MarketProfile/Overview/detail',
      query: {
        pageid: 'MarketProfilMobile',
        marketName: param?.data?.name || '',
        marketNameEn: param?.data?.nameEn?.toLowerCase() || '',
        fromPage: 'GlobalView',
      },
    });
  });

  // title点击
  const handleOnClickTitleGoProfile = () => {
    history.push({
      pathname: '/intelligence/marketProfile',
    });
  };
  return (
    <>
      <CardSlot
        className="dbi-global-view-revenue-growthmap"
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {`${changeValue} ${newzooTitle?.bottomLeft}`}
            <TipContext
              placement="left"
              tipText={newzooTitle?.tipMapText}
              className="dbi-global-view-revenue-icon"
            />
          </div>
        }
        iconList={[]}
        isAction
        getDetails={handleOnClickTitleGoProfile}
      >
        {marketLoading ? (
          <div style={{ height: '500px' }}>
            <Loading />
          </div>
        ) : (
          <EacherMap
            tooltipMapFun={tooltipMapFun}
            handleClickFun={handleClickFun}
            fullScreenTitle={`${changeValue} ${newzooTitle?.bottomLeft}`}
            dataRes={eacherMapList?.dataRes || []}
            mapRevenue={eacherMapList?.mapRevenue}
            mapGrowth={eacherMapList?.mapGrowth}
            height={500}
            marketLegend={eacherMapList?.marketLegend}
            visualMapColorFun={eacherMapList?.visualMapColorFun}
          />
        )}
      </CardSlot>
    </>
  );
};

export default memo(MarketEcharts);
